// src/lib/overview/utils.js

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const CATEGORY_OPTIONS = [
"Trust & Safety",
"Pricing & Value",
"Discovery",
"Quality & Reliability",
"Convenience",
"Capability & Fit",
"Other"];


const METHOD_LABELS = {
  user_feedback: "User feedback",
  interview_notes: "Interview notes",
  usability_testing: "Usability testing",
  survey_results: "Survey results",
  analytics_data: "Analytics data",
  research_materials: "Research materials",
  other: "Other"
};

const RESEARCH_TYPE_LABELS = {
  user_feedback: "User feedback",
  interview_notes: "Interview notes",
  usability_testing: "Usability testing",
  survey_results: "Survey results",
  analytics_data: "Analytics data",
  research_materials: "Research materials"
};

const SOURCE_TYPE_LABELS = {
  document: "Documents",
  screenshot: "Screenshots",
  structured: "Structured data",
  paste: "Paste"
};

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}
function safeObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}
function isNonEmptyString(v) {
  return typeof v === "string" && v.trim().length > 0;
}
function hasProblemDefinitionContent(pd) {
  return (
    isNonEmptyString(pd?.problem) ||
    isNonEmptyString(pd?.weWill) ||
    isNonEmptyString(pd?.successCriteria));

}
function formatDateLabel(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

const devWarnedKeys = new Set();
function devWarnOnce(key, message, extra) {
  if (!import.meta?.env?.DEV) return;
  if (devWarnedKeys.has(key)) return;
  devWarnedKeys.add(key);
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    if (extra !== undefined) void 0;else
    void 0;
  }
}

function normalizeConfidence(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "high" || v === "medium" || v === "low") return v;
  return "medium";
}

function isInsightApproved(insight) {
  const status = String(insight?.reviewStatus || "").trim().toLowerCase();
  return status === "approved" || status === "reviewed";
}

const OVERVIEW_OVERWRITE_MODES = [
{ id: "aiOnly", label: "Overwrite AI only" },
{ id: "everything", label: "Overwrite everything" },
{ id: "fillEmpty", label: "Fill empty only" }];


const NEXT_STEP_ACTIONS = [
"personas",
"interview-questions",
"research-questions",
"start-study",
"problem-definition",
"decisions",
"journey",
"insights"];


const makeRegenSelection = () => ({
  executiveSummary: true,
  synthesis: true,
  coreNeeds: true,
  primaryProblems: true,
  problemDefinition: true,
  strategicDirections: true,
  recommendedNextSteps: true,
  decisions: true
});

const SYNTHESIS_LIMITS = { patterns: 3, questions: 3, tensions: 2 };

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function makeOverviewHash(items) {
  const payload = items.
  map((it) => ({
    id: String(it?.id || ""),
    text: String(it?.text || "").trim(),
    groupId: String(it?.groupId || ""),
    evidenceCount: Number(it?.sourcesCount || 0),
    confidence: String(it?.evidenceStrength || ""),
    tags: safeArray(it?.tags).map((t) => String(t || "").trim()).sort()
  })).
  sort((a, b) => a.id.localeCompare(b.id));
  return hashString(JSON.stringify(payload)).toString(16);
}
function buildResearchItemLabel(researchItem) {
  const base = String(researchItem?.label || researchItem?.title || "").trim();
  if (base) return base;
  const dateLabel = formatDateLabel(researchItem?.createdAt);
  if (dateLabel) return dateLabel;
  return "Research item";
}

function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function inferEvidenceTypeFromText(text) {
  const t = String(text || "").trim();
  if (!t) return "observation";
  if (/[\"“”]/.test(t)) return "quote";
  if (
  /\b(i|we|my|our|me|us)\b/i.test(t) &&
  /\b(need|want|like|love|hate|feel|think|prefer|struggle|cant|can't|dont|don't|won't|would)\b/i.test(t))
  {
    return "quote";
  }
  if (
  /\b(%|percent|rate|ratio|average|avg|median|mean|conversion|retention|churn|nps|csat|dau|mau|arr|mrr)\b/i.test(t))
  {
    return "fact";
  }
  return "quote";
}

function computeEvidenceStrength(evidence) {
  const arr = Array.isArray(evidence) ? evidence : [];
  const count = arr.length;
  if (!count) return "none";
  const sources = new Set(arr.map((e) => e?.researchItemId).filter(Boolean));
  const sourceCount = sources.size;
  if (sourceCount >= 3 || count >= 4) return "high";
  if (sourceCount >= 2 || count >= 2 && count <= 3) return "medium";
  return "low";
}

// ✅ Only prompt when there is no evidence at all.
function shouldPromptEvidence(strength) {
  return strength === "none";
}

function categorizeInsight(text) {
  const t = String(text || "").toLowerCase();
  const has = (arr) => arr.some((k) => t.includes(k));

  if (
  has([
  "trust",
  "safe",
  "safety",
  "secure",
  "security",
  "verification",
  "verified",
  "authenticate",
  "fraud",
  "scam",
  "abuse",
  "privacy",
  "gdpr",
  "permission",
  "consent",
  "policy",
  "compliance",
  "liability",
  "insurance",
  "incident",
  "emergency",
  "report",
  "dispute",
  "chargeback",
  "moderation",
  "accountability",
  "transparency",
  "reviews",
  "rating",
  "reputation",
  "guarantee"]
  ))

  return "Trust & Safety";

  if (
  has([
  "price",
  "pricing",
  "cost",
  "fee",
  "subscription",
  "plan",
  "tier",
  "billing",
  "payment",
  "charge",
  "refund",
  "trial",
  "value",
  "affordable"]
  ))

  return "Pricing & Value";

  if (has(["find", "search", "discover", "browse", "filter", "sort", "taxonomy", "category", "navigation"]))
  return "Discovery";

  if (has(["quality", "reliable", "reliability", "bug", "crash", "error", "downtime", "performance", "latency"]))
  return "Quality & Reliability";

  if (has(["convenient", "convenience", "fast", "quick", "time", "steps", "friction", "effort", "schedule", "delay"]))
  return "Convenience";

  if (has(["match", "fit", "capability", "skill", "experience", "qualification", "requirements", "eligibility"]))
  return "Capability & Fit";

  return "Other";
}

function normalizeCategory(c, fallback = "Other") {
  const s = String(c || "").trim();
  if (!s) return fallback;
  const found = CATEGORY_OPTIONS.find((x) => x.toLowerCase() === s.toLowerCase());
  return found || fallback;
}

function normalizeTags(tags, fallbackText = "") {
  if (typeof tags === "string" && tags.trim()) return [normalizeCategory(tags, "Other")];

  const raw = safeArray(tags).
  filter(isNonEmptyString).
  map((t) => normalizeCategory(t, "Other"));

  const uniq = Array.from(new Set(raw));
  if (uniq.length) return uniq;

  return [normalizeCategory(categorizeInsight(fallbackText), "Other")];
}

function now() {
  return Date.now();
}

function stripCodeFences(raw = "") {
  return String(raw).
  trim().
  replace(/^```json/i, "").
  replace(/^```/i, "").
  replace(/```$/i, "").
  trim();
}

function extractFirstJsonObject(raw = "") {
  const s = String(raw || "");
  if (!s) return "";

  const trimmed = s.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return trimmed;

  const starts = ["{", "["].
  map((ch) => trimmed.indexOf(ch)).
  filter((i) => i >= 0);

  if (!starts.length) return "";
  const startIdx = Math.min(...starts);
  const str = trimmed.slice(startIdx);
  let depth = 0;
  let inString = false;
  let escape = false;
  const openChar = str[0];
  const closeChar = openChar === "{" ? "}" : "]";

  for (let i = 0; i < str.length; i++) {
    const ch = str[i];

    if (inString) {
      if (escape) escape = false;else
      if (ch === "\\") escape = true;else
      if (ch === '"') inString = false;
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === openChar) depth++;
    if (ch === closeChar) depth--;

    if (depth === 0) {
      return str.slice(0, i + 1).trim();
    }
  }

  return "";
}

function tryRepairJsonCommonMistakes(s) {
  let out = String(s || "").trim();
  if (!out) return "";

  out = out.replace(/,\s*([}\]])/g, "$1");
  out = out.replace(/\u00a0/g, " ").replace(/\u200b/g, "");

  return out.trim();
}

function safeJsonParse(raw) {
  const cleaned = stripCodeFences(raw);
  const extracted = extractFirstJsonObject(cleaned) || cleaned.trim();
  const repaired = tryRepairJsonCommonMistakes(extracted);
  return JSON.parse(repaired);
}

function evidenceStrengthClass(level) {
  if (level === "high") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (level === "medium") return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (level === "low") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function confidencePillClass(level) {
  if (level === "high") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (level === "medium") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-rose-200 bg-rose-50 text-rose-700";
}

function formatMentions(count) {
  const n = Number.isFinite(Number(count)) ? Number(count) : 0;
  return `${n} evidence`;
}

function parseThemeSuffix(text) {
  const raw = String(text || "").trim();
  const match = raw.match(/\[Theme:\s*([^\]]+)\]\s*$/);
  if (!match) return { text: raw, theme: "" };
  const cleaned = raw.replace(match[0], "").trim();
  return { text: cleaned || raw, theme: String(match[1] || "").trim() };
}

function buildApprovedInsightsSnapshot({ project, approvedInsights, rawInsightById, insightGroups, tagVocabulary, tagThemes, overview }) {
  const snapshotInsights = approvedInsights.
  map((ins) => {
    const raw = rawInsightById.get(String(ins.id));
    const category = typeof raw?.category === "string" ? raw.category : "";
    const evidence = safeArray(raw?.evidence).
    map((ev) => {
      if (!ev || typeof ev !== "object") return null;
      const text = String(ev.text || "").trim();
      if (!text) return null;
      return {
        type: String(ev.type || "").trim() || "observation",
        text,
        researchItemId: typeof ev.researchItemId === "string" && ev.researchItemId.trim() ? ev.researchItemId.trim() : null
      };
    }).
    filter(Boolean);
    const status =
    ins.originType === "manual" ? "manual" : ins.originType === "modified" ? "modified" : "generated";
    const origin = ins.originType === "manual" || ins.originType === "modified" ? "user" : "ai";
    return {
      id: String(ins.id),
      text: String(ins.text || "").trim(),
      category,
      confidence: String(ins.evidenceStrength || "medium"),
      tags: safeArray(ins.tags).map((t) => String(t || "").trim()).filter(Boolean),
      sourcesCount: Number(ins.sourcesCount || 0),
      evidence,
      origin,
      status
    };
  }).
  sort((a, b) => a.id.localeCompare(b.id));

  const snapshot = {
    project: {
      id: String(project?.id || ""),
      name: String(project?.meta?.projectName || project?.projectName || "").trim(),
      productDescription: String(project?.meta?.productDescription || project?.productDescription || "").trim(),
      targetAudience: String(project?.meta?.targetAudience || project?.targetAudience || "").trim(),
      industry: String(project?.meta?.industry || project?.industry || "").trim()
    },
    approvedInsights: snapshotInsights,
    insightGroups: safeObj(insightGroups),
    tagVocabulary: safeObj(tagVocabulary),
    tagThemes: safeObj(tagThemes),
    existingOverview: {
      executiveSummary: String(overview?.executiveSummary || "").trim(),
      synthesis: safeObj(overview?.synthesis),
      coreNeedsByTheme: safeObj(overview?.coreNeedsByTheme),
      primaryProblems: safeArray(overview?.primaryProblems),
      strategicDirections: safeArray(overview?.opportunityAreas),
      problemDefinition: safeObj(overview?.problemDefinition),
      recommendedNextSteps: safeArray(overview?.recommendedNextSteps),
      decisions: safeArray(overview?.decisions)
    },
    overwritePolicy: {
      allowOverwrite: ["generated_empty_or_not_dirty_only"],
      neverOverwrite: ["manual", "modified", "dirty"]
    }
  };

  const hashPayload = snapshotInsights.map((ins) => ({
    id: ins.id,
    text: ins.text,
    confidence: ins.confidence,
    tags: [...ins.tags].sort(),
    evidence: ins.evidence.map((ev) => ({
      type: ev.type,
      text: ev.text,
      researchItemId: ev.researchItemId || ""
    }))
  }));

  const snapshotHash = hashString(JSON.stringify(hashPayload)).toString(16);

  return { snapshot, snapshotHash };
}

function clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function ratio(n, d) {
  const nn = Number(n);
  const dd = Number(d);
  if (!Number.isFinite(nn) || !Number.isFinite(dd) || dd <= 0) return 0;
  return clamp(nn / dd, 0, 1);
}

export {
  CATEGORY_OPTIONS,
  METHOD_LABELS,
  RESEARCH_TYPE_LABELS,
  SOURCE_TYPE_LABELS,
  devWarnedKeys,
  OVERVIEW_OVERWRITE_MODES,
  NEXT_STEP_ACTIONS,
  makeRegenSelection,
  SYNTHESIS_LIMITS,
  safeArray,
  safeObj,
  isNonEmptyString,
  hasProblemDefinitionContent,
  formatDateLabel,
  devWarnOnce,
  normalizeConfidence,
  isInsightApproved,
  hashString,
  makeOverviewHash,
  buildResearchItemLabel,
  uid,
  inferEvidenceTypeFromText,
  computeEvidenceStrength,
  shouldPromptEvidence,
  categorizeInsight,
  normalizeCategory,
  normalizeTags,
  now,
  stripCodeFences,
  extractFirstJsonObject,
  tryRepairJsonCommonMistakes,
  safeJsonParse,
  evidenceStrengthClass,
  confidencePillClass,
  formatMentions,
  parseThemeSuffix,
  buildApprovedInsightsSnapshot,
  clamp,
  ratio };
